import { useAtom } from 'jotai';
import { localeSelectionAtom } from 'src/services/state/atoms/localeSelection';
import { useSession } from 'src/services/state/providers/SessionProvider';

import { useLocale, useTranslations } from 'next-intl';
import { useParams, useSearchParams } from 'next/navigation';
import React, { useState, useTransition } from 'react';

import { localeObjects as configLocales } from '../i18n/config';
import { usePathname, useRouter } from '../i18n/routing';
import Icon from './Icon';

/**
 * LocaleSwitcher component provides a dropdown menu for switching website languages.
 *
 * @returns {JSX.Element} The rendered LocaleSwitcher component.
 */
export default function LocaleSwitcher() {
  const t = useTranslations('components.Header');
  const router = useRouter();
  const [, startTransition] = useTransition();
  const pathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const toggleIsOpen = () => setIsOpen(!isOpen);
  const language = useLocale();
  const session = useSession();
  const userId = session?.user?.id;
  const [localeSelectionMap, setLocaleSelectionMap] = useAtom(localeSelectionAtom);

  const handleLanguageChange = (symbol: string) => {
    if (userId) {
      setLocaleSelectionMap({ ...localeSelectionMap, [userId]: symbol });
    }
    startTransition(() => {
      router.replace(
        // @ts-expect-error -- TypeScript will validate that only known `params`
        // are used in combination with a given `pathname`. Since the two will
        // always match for the current route, we can skip runtime checks.
        { pathname, params, query: Object.fromEntries(searchParams.entries()) },
        { locale: symbol }
      );
    });
    setIsOpen(false);
  };
  return (
    <div className="usa-language-container usa-language--medium">
      <ul className="usa-language__primary usa-accordion">
        <li className="usa-language__primary-item">
          <button
            className="usa-button width-auto usa-button--unstyled usa-button--inverse usa-button--outline usa-language__link"
            type="button"
            aria-expanded={isOpen ? 'true' : 'false'}
            aria-controls="language-options"
            onClick={toggleIsOpen}
            data-testid="language-selector-toggle"
          >
            <Icon className="margin-right-05 position-relative text-middle" name="language" size={3} />
            {t('languages')}
          </button>
          <ul
            id="language-options"
            className="usa-language__submenu"
            hidden={!isOpen}
            data-testid="language-selector-menu"
          >
            {configLocales.map(({ label, symbol }) => (
              <li className={`usa-language__submenu-item ${language === symbol ? 'selected' : ''}`} key={symbol}>
                <button
                  className="usa-button width-full text-left"
                  type="button"
                  onClick={() => handleLanguageChange(symbol)}
                  title={label}
                  data-testid={`language-selector-${symbol}`}
                  tabIndex={0}
                >
                  <span className="pretranslated-multilanguage-content" lang={symbol}>
                    {label}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </li>
      </ul>
    </div>
  );
}
